import { Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import {
  selectAdditionalInfo,
  selectBookedBy,
  selectDestination,
  selectEditJobId,
  selectGuestPaymentMethodId,
  selectOrderedLocations,
  selectPassengerDetails,
  selectPaymentMethod,
  selectPickup,
  selectIsJobVerified,
  selectPickupFlightNumber,
  selectDepartureFlightNumber,
  selectDepartureTerminalSource,
  selectPickupTerminalSource,
  selectReference,
} from "@/redux/slices/clientBooking/selectors";
import { clsx } from "@/utils";
import { useAttachmentDisplayNames } from "./useAttachmentDisplayNames";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { EditPencil } from "./summary/EditPencil";
import { PaymentMethodRow } from "./summary/PaymentMethodRow";
import { PricedBookingSummary } from "./summary/PricedBookingSummary";
import { RouteTimeline } from "./summary/RouteTimeline";
import { ScopedClientCard } from "./summary/ScopedClientCard";
import { boxState, SummaryBox } from "./summary/SummaryBox";
import { TripDetailsBody } from "./summary/TripDetailsBody";
import { TrustNote } from "./summary/TrustNote";

export type BookingStep = "address" | "trip-details" | "payment";

interface BookingSummaryPanelProps {
  className?: string;
  /** Hide the internal "Booking Summary" heading when a wrapping panel already provides a titled header. */
  hideHeading?: boolean;
  /** Which step to highlight. */
  activeStep?: BookingStep;
  /**
   * Steps the user has gone through (advanced past). Drives whether a box shows its data + edit
   * pencil: a box is populated only when it's the active step (live preview) or it's been completed.
   */
  completedSteps?: BookingStep[];
  /** Jump back to a wizard step — wires every box's edit pencil (and the priced summary's). */
  onEditStep?: (step: BookingStep) => void;
  /**
   * Horizontal (side) padding for the panel's content, applied inside the panel rather than by the
   * scroll container — this lets the Payment step's sticky footer + its divider span the full panel
   * width while its content still lines up with everything above. Defaults to `px-5` (20px).
   */
  edgeClassName?: string;
}

/**
 * The "Booking Summary" content. Persistent right sidebar on lg+ and a full-screen toggled view on
 * tablet/mobile. On the Payment step it becomes the priced summary (draft cards + live quote +
 * Book Trip); on earlier steps the boxes reflect live booking data from clientBookingSlice: the
 * Address route and the selected passenger (defaults to the account holder).
 *
 * Both variants share the same row components (RouteTimeline / TripCoreRows / DetailRow) so the
 * summary reads identically on every step. Thin dispatcher on purpose — the two variants live in
 * child components so each keeps an unconditional hook order while the panel switches between them.
 */
export const BookingSummaryPanel = ({
  className,
  hideHeading = false,
  activeStep = "address",
  completedSteps = [],
  onEditStep,
  edgeClassName = "px-5",
}: BookingSummaryPanelProps) => {
  const viewOnly = useBookingViewOnly();
  const isEdit = useAppSelector(selectEditJobId) != null;
  // View-only shows the finished booking's price on every step (it's fixed, not step-dependent), so
  // always use the priced summary — which renders the stored price and the read-only cards.
  // Edit shows it on EVERY step too: the draft is fully hydrated, so the quote resolves regardless of
  // step and the user can tweak one field then hit "Update Booking" straight from the summary — no need
  // to walk to Payment. (Create/guest still reveal the priced summary only on the Payment step.)
  if (activeStep === "payment" || viewOnly || isEdit) {
    return (
      <PricedBookingSummary
        className={className}
        hideHeading={hideHeading}
        activeStep={activeStep}
        onEditStep={onEditStep}
        edgeClassName={edgeClassName}
      />
    );
  }
  return (
    <DraftBookingSummary
      className={className}
      hideHeading={hideHeading}
      activeStep={activeStep}
      completedSteps={completedSteps}
      onEditStep={onEditStep}
      edgeClassName={edgeClassName}
    />
  );
};

/** The pre-payment variant — live draft boxes (address route, passenger) + trust items. */
const DraftBookingSummary = ({
  className,
  hideHeading,
  activeStep,
  completedSteps = [],
  onEditStep,
  edgeClassName,
}: Pick<BookingSummaryPanelProps, "className" | "hideHeading" | "activeStep" | "completedSteps" | "onEditStep" | "edgeClassName">) => {
  const pickup = useAppSelector(selectPickup);
  const destination = useAppSelector(selectDestination);
  const locations = useAppSelector(selectOrderedLocations);
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const pickupTerminalSource = useAppSelector(selectPickupTerminalSource);
  const departureFlightNumber = useAppSelector(selectDepartureFlightNumber);
  const departureTerminalSource = useAppSelector(selectDepartureTerminalSource);
  const jobVerified = useAppSelector(selectIsJobVerified);
  const bookedBy = useAppSelector(selectBookedBy);
  const reference = useAppSelector(selectReference);
  const additionalInfo = useAppSelector(selectAdditionalInfo);
  const passengers = useAppSelector(selectPassengerDetails);
  const paymentMethod = useAppSelector(selectPaymentMethod);
  const guestPaymentMethodId = useAppSelector(selectGuestPaymentMethodId);
  // Existing (view/edit) attachments minus staged removals, plus newly-picked files — kept in sync with
  // the Other Details panel's Attachments field.
  const attachmentNames = useAttachmentDisplayNames();

  const hasRoute = !!(pickup && destination);

  // A box is populated (data + edit pencil) only when it's the active step — a live preview as the
  // user fills it — or the user has gone through it. This is what stops Trip Details from looking
  // pre-populated on the Address step before it's been visited.
  const addressActive = activeStep === "address";
  const tripActive = activeStep === "trip-details";
  const addressDone = completedSteps.includes("address");
  const tripDone = completedSteps.includes("trip-details");
  // Payment is never the active step in the draft summary; it's "done" once the user has reached it,
  // which is what keeps the chosen card (+ edit pencil) showing here after navigating back.
  const paymentDone = completedSteps.includes("payment");
  const showAddress = addressActive || addressDone;
  const showTrip = tripActive || tripDone;

  return (
    <div className={clsx("flex w-full flex-col pb-5", edgeClassName, className)}>
      {!hideHeading && (
        <Typography variant="title" className="mb-4 block">
          Booking Summary
        </Typography>
      )}

      <ScopedClientCard />

      <div className="flex flex-col gap-2">
        <SummaryBox
          icon="Location"
          title="Address"
          state={boxState(addressActive, addressDone)}
          // Edit pencil once the step is done — but never on the step you're already editing.
          action={addressDone && !addressActive && onEditStep && <EditPencil label="Edit address" onClick={() => onEditStep("address")} />}
        >
          {showAddress && hasRoute && (
            <RouteTimeline
              locations={locations}
              pickupFlightNumber={pickupFlightNumber}
              pickupTerminalSource={pickupTerminalSource}
              departureFlightNumber={departureFlightNumber}
              departureTerminalSource={departureTerminalSource}
              jobVerified={jobVerified}
            />
          )}
        </SummaryBox>

        <SummaryBox
          icon="User"
          title="Trip Details"
          state={boxState(tripActive, tripDone)}
          action={tripDone && !tripActive && onEditStep && <EditPencil label="Edit trip details" onClick={() => onEditStep("trip-details")} />}
        >
          {/* Body only once the step is active (live preview) or gone through — so it isn't pre-populated. */}
          {showTrip && (
            <TripDetailsBody
              bookedBy={bookedBy}
              reference={reference}
              additionalInfo={additionalInfo}
              attachmentNames={attachmentNames}
              passengers={passengers}
              highlighted={tripActive}
            />
          )}
        </SummaryBox>

        <SummaryBox
          icon="Card"
          title="Payment"
          state={boxState(false, paymentDone)}
          action={paymentDone && onEditStep && <EditPencil label="Edit payment" onClick={() => onEditStep("payment")} />}
        >
          {paymentDone &&
            (paymentMethod ? (
              <PaymentMethodRow method={paymentMethod} />
            ) : guestPaymentMethodId ? (
              <Typography variant="small-regular" className="text-neutral-dark-gray">
                Credit card ready
              </Typography>
            ) : null)}
        </SummaryBox>
      </div>

      <TrustNote className="mt-4" />
    </div>
  );
};
